import React, { useEffect, useState } from 'react';
import { navigate, safeFetch } from './App';
import { Users, CheckCircle, XCircle, Loader, LogIn } from 'lucide-react';

export const JoinZone = ({ chatId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [alreadyMember, setAlreadyMember] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [zoneInfo, setZoneInfo] = useState(null);
    const [inviteCode, setInviteCode] = useState(null);

    useEffect(() => {
        const handleJoinZone = async () => {
            // Get invite code from URL
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('invite');

            if (!code) {
                setError('קוד הזמנה לא תקין');
                setLoading(false);
                return;
            }

            setInviteCode(code);

            try {
                const response = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/join-zone', chatId, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        invite_code: code
                    }),
                });

                const data = await response.json();

                if (data.error) {
                    setError(data.error);
                } else if (data.status === 'owner' || data.status === 'already_member') {
                    setAlreadyMember(true);
                    setZoneInfo(data);
                } else {
                    setSuccess(true);
                    setZoneInfo(data);
                }
            } catch (err) {
                console.error('❌ Failed to join zone:', err);
                setError('שגיאה בהצטרפות לקהילה');
            } finally {
                setLoading(false);
            }
        };

        if (chatId) {
            handleJoinZone();
        } else {
            setIsGuest(true);
            setLoading(false);
        }
    }, [chatId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-surface via-white to-surface" dir="rtl">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center space-y-6 border border-line-subtle">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-warning-500 to-warning-600 rounded-full flex items-center justify-center">
                        <Loader size={40} className="text-white animate-spin" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-ink-strong">מצטרף לקהילה...</h2>
                    <p className="text-ink-muted">אנא המתן בזמן שאנו מעבדים את ההזמנה שלך</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-surface via-white to-surface" dir="rtl">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center space-y-6 border border-line-subtle">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-danger-500 to-danger-600 rounded-full flex items-center justify-center">
                        <XCircle size={40} className="text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-ink-strong">שגיאה בהצטרפות</h2>
                    <p className="text-ink-muted">{error}</p>
                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('zones')}
                            className="w-full px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                        >
                            חזור לקהילות
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isGuest) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-surface via-white to-surface" dir="rtl">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center space-y-6 border border-line-subtle">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-info-500 to-info-600 rounded-full flex items-center justify-center">
                        <LogIn size={40} className="text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-ink-strong">נדרשת הרשמה</h2>
                    <p className="text-ink-muted">כדי להצטרף לקהילה, יש להתחבר או להירשם תחילה</p>

                    <div className="space-y-3">
                        <button
                            onClick={() => {
                                // Save the current URL so user returns here after login
                                sessionStorage.setItem('redirectAfterLogin', window.location.search);
                                // Clear guest session to trigger login prompt
                                localStorage.removeItem('chat_id');
                                // Navigate to root path to trigger login screen (avoids isSharedLink detection)
                                window.location.href = window.location.origin;
                            }}
                            className="w-full px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                        >
                            התחבר / הירשם
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (alreadyMember && zoneInfo) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-surface via-white to-surface" dir="rtl">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center space-y-6 border border-line-subtle">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-warning-500 to-warning-600 rounded-full flex items-center justify-center">
                        <Users size={40} className="text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-ink-strong">את/ה כבר חלק מהקהילה!</h2>
                    <p className="text-ink-muted">אין צורך להצטרף שוב, את/ה כבר {zoneInfo.status === 'owner' ? 'מנהל/ת' : 'חבר/ה'} בקהילה הזו</p>

                    <div className="bg-gradient-to-br from-warning-50 to-warning-100/50 rounded-xl p-6 border border-warning-100">
                        <div className="flex items-center gap-4 justify-center mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-600 flex items-center justify-center flex-shrink-0">
                                <Users size={24} className="text-white" strokeWidth={2} />
                            </div>
                        </div>
                        <h3 className="text-xl font-bold text-ink-strong mb-2">{zoneInfo.zone_name}</h3>
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('zone-details', `zone=${encodeURIComponent(zoneInfo.zone_id)}`)}
                            className="w-full px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                        >
                            עבור לקהילה
                        </button>
                        <button
                            onClick={() => navigate('zones')}
                            className="w-full px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                        >
                            חזור לכל הקהילות
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (success && zoneInfo) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-surface via-white to-surface" dir="rtl">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center space-y-6 border border-line-subtle">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-success-500 to-success-600 rounded-full flex items-center justify-center">
                        <CheckCircle size={40} className="text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-ink-strong">הצטרפת בהצלחה!</h2>

                    <div className="bg-gradient-to-br from-brand-50 to-brand-100/50 rounded-xl p-6 border border-brand-200">
                        <div className="flex items-center gap-4 justify-center mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-600 flex items-center justify-center flex-shrink-0">
                                <Users size={24} className="text-white" strokeWidth={2} />
                            </div>
                        </div>
                        <h3 className="text-xl font-bold text-ink-strong mb-2">{zoneInfo.zone_name}</h3>
                        <p className="text-sm text-ink-muted">עכשיו את/ה חלק מהקהילה</p>
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('zone-details', `zone=${encodeURIComponent(zoneInfo.zone_id)}`)}
                            className="w-full px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                        >
                            עבור לקהילה
                        </button>
                        <button
                            onClick={() => navigate('zones')}
                            className="w-full px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                        >
                            חזור לכל הקהילות
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};
